import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Textarea,
  Alert
} from '@cloudscape-design/components';
import { blissApi } from './blissApi.js';

const STORAGE_KEY = 'bliss-serp-api-keys';

/** Whole days from today to an ISO date string (UTC-safe). Null if unparseable. */
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr + 'T00:00:00Z');
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.ceil((target.getTime() - todayUTC) / (1000 * 60 * 60 * 24));
}

/**
 * Load saved keys from localStorage.
 */
export function getSavedKeys() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? stored.split(',').map(k => k.trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

export default function SerpKeysModal({ visible, onDismiss }) {
  const [keysText, setKeysText] = useState('');
  const [balances, setBalances] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (visible) {
      const keys = getSavedKeys();
      setKeysText(keys.join(', '));
      setBalances([]);
      setSaved(false);
    }
  }, [visible]);

  const handleCheckBalance = async () => {
    const keys = keysText.split(',').map(k => k.trim()).filter(Boolean);
    if (keys.length === 0) return;

    setChecking(true);
    setBalances([]);

    const results = [];
    for (const key of keys) {
      try {
        const data = await blissApi.checkSerpKey(key);
        const planLeft = data.plan_searches_left || 0;
        const perMonth = data.searches_per_month || 0;
        const extraTotal = data.extra_credits || 0;
        const totalLeft = data.total_searches_left || 0;
        results.push({
          key: key.slice(0, 8) + '...' + key.slice(-4),
          plan: data.plan_name,
          planLeft,                                  // plan searches remaining
          perMonth,                                  // monthly plan quota
          extraLeft: Math.max(0, totalLeft - planLeft), // extra credits remaining
          extraTotal,                                // extra credits granted
          totalLeft,                                 // total usable searches left
          renewalDate: data.plan_renewal_date || null,
          error: null,
        });
      } catch (err) {
        results.push({
          key: key.slice(0, 8) + '...' + key.slice(-4),
          plan: 'Unknown',
          planLeft: 0,
          perMonth: 0,
          extraLeft: 0,
          extraTotal: 0,
          totalLeft: 0,
          error: err.message,
        });
      }
    }

    setBalances(results);
    setChecking(false);
  };

  const handleSave = () => {
    const keys = keysText.split(',').map(k => k.trim()).filter(Boolean);
    localStorage.setItem(STORAGE_KEY, keys.join(','));
    setSaved(true);
    setTimeout(() => onDismiss(), 800);
  };

  const totalRemaining = balances.reduce((sum, b) => sum + (b.totalLeft || 0), 0);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="🔑 SerpAPI Keys"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          Paste one or more SerpAPI keys (comma-separated). These will be used for your next job run.
          When one key is exhausted, the job automatically switches to the next.
        </Box>

        <Textarea
          value={keysText}
          onChange={({ detail }) => { setKeysText(detail.value); setSaved(false); }}
          placeholder="key1abc123..., key2def456..., key3ghi789..."
          rows={3}
        />

        <Button onClick={handleCheckBalance} loading={checking} iconName="status-info">
          Check Balance
        </Button>

        {balances.length > 0 && (
          <SpaceBetween size="s">
            {balances.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  {b.error ? (
                    <Alert type="error">Key {i + 1} ({b.key}): {b.error}</Alert>
                  ) : (
                    <SpaceBetween size="xxxs">
                      <Box fontSize="body-s">
                        <strong>Key {i + 1}</strong> ({b.key}) — {b.plan}
                      </Box>
                      <Box fontSize="body-s" color={b.totalLeft < 20 ? 'text-status-error' : 'text-status-success'}>
                        Balance — Plan: {b.planLeft.toLocaleString()}/{b.perMonth.toLocaleString()}
                        {b.extraTotal > 0 && <> &nbsp;·&nbsp; Extra Credits: {b.extraLeft.toLocaleString()}/{b.extraTotal.toLocaleString()}</>}
                        &nbsp;·&nbsp; <strong>Total Left: {b.totalLeft.toLocaleString()}</strong>
                      </Box>
                      {b.renewalDate && (() => {
                        const d = daysUntil(b.renewalDate);
                        return (
                          <Box fontSize="body-s">
                            Expiring on:{' '}
                            <strong style={{ color: '#d91515' }}>{b.renewalDate}</strong>
                            {d != null && <> (<strong style={{ color: '#d91515' }}>{d} {d === 1 ? 'day' : 'days'} left</strong>)</>}
                          </Box>
                        );
                      })()}
                    </SpaceBetween>
                  )}
                </div>
                <Button
                  variant="icon"
                  iconName="close"
                  ariaLabel={`Remove key ${i + 1}`}
                  onClick={() => {
                    const keys = keysText.split(',').map(k => k.trim()).filter(Boolean);
                    keys.splice(i, 1);
                    setKeysText(keys.join(', '));
                    setBalances(prev => prev.filter((_, idx) => idx !== i));
                  }}
                />
              </div>
            ))}
            <Box fontWeight="bold" fontSize="heading-s">
              Total available: {totalRemaining} searches
            </Box>
          </SpaceBetween>
        )}

        {saved && <Alert type="success">Keys saved! They'll be used for your next job.</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
